import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base: layout, typography, focus ring, disabled state, and sane icon sizing.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ' +
    'transition-[color,background-color,border-color,box-shadow,transform] duration-150 ' +
    'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    'active:translate-y-px select-none',
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-b from-[oklch(0.86_0.12_88)] to-primary text-primary-foreground gold-glow hover:brightness-105 active:brightness-95',
        secondary:
          'glass text-foreground hover:bg-muted/60',
        ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
        danger:
          'bg-danger text-danger-foreground shadow-sm hover:brightness-105 active:brightness-95',
        outline:
          'border border-border bg-transparent text-foreground hover:bg-muted',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'size-10 p-0',
      },
      full: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      full: false,
    },
  },
);

export interface ButtonProps
  extends ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  /** Render as the child element instead of a <button>, for links etc. */
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  full,
  asChild = false,
  type = 'button',
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot : 'button';
  return (
    <Component
      // Default to type="button": an unspecified button inside a form submits it,
      // which is almost never what is wanted.
      {...(asChild ? {} : { type })}
      className={cn(buttonVariants({ variant, size, full }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
